import i18next, { type i18n } from "i18next";
import type { Locale } from "./locale.js";
// The tenant overlays are STATIC imports: under 1 kB for all of them, against the cost of a second async hop at
// exactly the moment a language is switching. `specializedLanguageConfigs` in the legacy (ADR-048 D4).
import appEn from "./messages/app/en.json" with { type: "json" };
import appDe from "./messages/app/de.json" with { type: "json" };
import takedaDe from "./messages/overlays/de.takeda.json" with { type: "json" };
import abzbraunauDe from "./messages/overlays/de.abzbraunau.json" with { type: "json" };

/**
 * The i18next runtime (ADR-048 Phase 2).
 *
 * Three decisions from the ADR are encoded here, each of which a panel showed is silently wrong if changed:
 *
 * 1. **ONE namespace.** The 37 top-level keys are key-path PREFIXES, not i18next namespaces. 99 % of the legacy's
 *    350 glossary references cross a top-level boundary, so `$t(manual.raw)` must resolve as a key path. Declaring
 *    them as namespaces renders the literal "Dieses manual.raw" across hundreds of sites (D2/D3).
 * 2. **Single-brace interpolation.** The catalogue writes `{manualName}`; i18next defaults to `{{ }}`. On the
 *    default, all 120 placeholders print their own text to the user (D3).
 * 3. **A locale is never installed without its tenant overlay** — see {@link installLocale}.
 */

/** The single namespace. Named `translation` because that is i18next's own default, so nothing has to opt in. */
export const NS = "translation";

/**
 * Per-locale code splitting (D2). Written as an explicit map rather than a template-literal `import()` so the
 * chunks are statically analysable and a missing locale is a TYPE error rather than a 404 at runtime.
 *
 * This is where the catalogue's ~22 kB gzipped actually lands: a German user never downloads the English one.
 */
const CATALOGUES: Record<Locale, () => Promise<{ default: object }>> = {
  en: () => import("./messages/en.json"),
  de: () => import("./messages/de.json"),
};

/**
 * Strings this rewrite added, which the legacy never had (ADR-048 Phase 3).
 *
 * A THIRD layer exists because the legacy catalogues are GENERATED: `extract-legacy-locale.mjs --check` re-derives
 * them byte-for-byte, so a key hand-written into `en.json` would be silently erased by the next extraction. Keeping
 * the generated mirror pristine is the whole basis of that guarantee, so new strings need their own file.
 *
 * Small by design, and it stays small: most of what the UI needs already exists in the 1 023 legacy strings, and
 * reaching for those is what makes the rewrite a PARITY exercise rather than a re-authoring. This layer is for
 * genuinely new UI — the theme picker, the filter chips — and for deliberately correcting a legacy string (the
 * English header reads "Facilityselection", missing its space).
 *
 * Keys live in their natural namespaces rather than behind an `app.` prefix: a call site should ask for
 * `facilitySelection.facilityHeader` and not care which file the words came from. Which layer owns a key is a
 * storage detail, and the test suite lists every deliberate override so it stays reviewed rather than accidental.
 */
const APP_ADDITIONS: Record<Locale, object> = { en: appEn, de: appDe };

/** Tenant terminology, by realm then locale. Only Takeda has any; ABZ Braunau's file is deliberately empty. */
const OVERLAYS: Record<string, Partial<Record<Locale, object>>> = {
  takeda: { de: takedaDe },
  abzbraunau: { de: abzbraunauDe },
};

/**
 * Install a locale's catalogue **and**, in the same step, everything layered on top of it.
 *
 * Three layers, in this order — each one may correct the one below:
 *   1. the generated legacy catalogue (1 023 strings, never hand-edited),
 *   2. {@link APP_ADDITIONS} — strings this rewrite added,
 *   3. the tenant's terminology overlay, which must win so a realm's own words survive both.
 *
 * **The atomicity is the point, and it is the fix for the plan panel's High finding.** An earlier design registered
 * the overlay once "at boot" for the boot-resolved locale. A Takeda user who booted in English and switched to
 * Deutsch then got *base* German — "Handlungsanweisung" silently gone — because the `de` overlay had never been
 * merged. Tying the overlay to the locale LOAD makes that state unreachable: there is no path that installs a base
 * without its overlay, and no `languageChanged` subscription to forget.
 *
 * **Everything is installed through `addResourceBundle`, and `init` is given `resources: {}` — deliberately.**
 * Measured, because the two paths differ and only one is safe: an object passed to `init({ resources })` is held
 * BY REFERENCE, so a later `addResourceBundle(…, deep: true)` merges into it and rewrites it. A dynamically
 * imported JSON module is a singleton cached for the page's lifetime, so that path leaves `de.json` itself reading
 * Takeda's words for every later reader. `addResourceBundle` instead deep-COPIES what it is handed, leaving the
 * caller's object untouched — verified in both directions, and pinned by a test so a change to that default
 * surfaces here rather than as one tenant's terminology leaking into another's page.
 */
export async function installLocale(instance: i18n, locale: Locale, realm?: string): Promise<void> {
  if (!instance.hasResourceBundle(locale, NS)) {
    const mod = await CATALOGUES[locale]();
    instance.addResourceBundle(locale, NS, mod.default, /* deep */ true, /* overwrite */ true);
    // Legacy first, then this rewrite's own strings on top. Order matters: the app layer exists partly to CORRECT
    // a legacy string, which it can only do by landing after it.
    instance.addResourceBundle(locale, NS, APP_ADDITIONS[locale], /* deep */ true, /* overwrite */ true);
  }
  const overlay = realm ? OVERLAYS[realm]?.[locale] : undefined;
  // `deep: true, overwrite: true` — MERGE over the base. A replace would drop the 1 023 strings the overlay does
  // not mention, leaving a 16-key catalogue.
  if (overlay) instance.addResourceBundle(locale, NS, overlay, /* deep */ true, /* overwrite */ true);
}

/**
 * Build the app's i18next instance with `locale` ready to render.
 *
 * Awaited before the first paint on purpose. Every visible string comes from here, so mounting first would render
 * the untranslated key set and swap it — a wrong-language flash, which is the same class of defect as the
 * wrong-brand flash `theming.md` forbids. The cost is one same-origin chunk for the active language only.
 */
export async function createI18n(locale: Locale, realm?: string): Promise<i18n> {
  const instance = i18next.createInstance();
  await instance.init({
    lng: locale,
    // No cross-language fallback: a key missing from German must surface as a missing key in tests, not silently
    // render English to a German user, which nobody reports as a bug — they just read the English.
    fallbackLng: false,
    ns: [NS],
    defaultNS: NS,
    // EMPTY on purpose — see installLocale. An object handed to `init` is kept by reference and would be
    // rewritten in place by the first overlay merge.
    resources: {},
    // React already escapes everything it renders; i18next escaping again would double-encode the catalogue's
    // embedded markup (`<b>{manualName}</b>` appears throughout the legacy strings).
    interpolation: { escapeValue: false, prefix: "{", suffix: "}" },
    // We control readiness ourselves — `createI18n` awaits the catalogue BEFORE the first mount, and
    // `switchLocale` installs before it changes language. react-i18next's default is to SUSPEND until it considers
    // the namespace ready, and there is no Suspense boundary above the header, so a subtree would silently render
    // as nothing rather than as text. Measured: the whole header came back empty.
    react: { useSuspense: false },
  });
  await installLocale(instance, locale, realm);
  return instance;
}

/**
 * The most recent switch requested per instance. A `WeakMap` so an instance that goes away takes its token with it.
 */
const latestRequest = new WeakMap<i18n, symbol>();

/**
 * Switch languages at runtime. Loads the catalogue if this is the locale's first use, then changes.
 *
 * Install BEFORE `changeLanguage`, never after: i18next re-renders its subscribers synchronously on the change,
 * so the reverse order paints one frame of missing keys.
 *
 * **Last request wins, not last to LOAD** (diff panel). The picker fires these without awaiting, and the work each
 * one does is wildly uneven: switching to an already-loaded language skips the import entirely, while a first-time
 * language waits on a network chunk. So picking Deutsch and then changing your mind back to English resolves
 * English first and lets the slower German request land afterwards — leaving the runtime in German while the
 * picker, `<html lang>` and the stored preference all say English. The token makes a superseded request return
 * without touching the language; its catalogue stays installed, which is only a warm cache.
 */
export async function switchLocale(instance: i18n, locale: Locale, realm?: string): Promise<void> {
  const token = Symbol(locale);
  latestRequest.set(instance, token);
  await installLocale(instance, locale, realm);
  if (latestRequest.get(instance) !== token) return;
  await instance.changeLanguage(locale);
}
